import { Controller } from "@hotwired/stimulus"
// The light player is svg-only, which is all these cards need, and roughly
// half the size of the full lottie-web build.
import lottie from "lottie-web/build/player/lottie_light"

// Learner Fact or Fiction block (TMS-769): one statement at a time, the two
// Lottie cards play a hover loop on pointer/focus and a correct/wrong result
// animation on answer, with the matching feedback snackbar. Previous/Next
// steps through statements and answers can be changed at any time. All state
// is client-side only — nothing is persisted.
export default class extends Controller {
  static targets = ["question", "card", "progressLabel", "progressFill", "previousButton", "nextButton"]

  connect() {
    this.animations = new Map()
    this.cardTargets.forEach((card) => this.loadHoverAnimation(card))
  }

  disconnect() {
    this.animations.forEach((animation) => animation.destroy())
    this.animations.clear()
  }

  playHover(event) {
    const animation = this.animations.get(event.currentTarget)
    if (!animation || animation.kind !== "hover") return

    animation.setDirection(1)
    animation.play()
  }

  rewindHover(event) {
    const animation = this.animations.get(event.currentTarget)
    if (!animation || animation.kind !== "hover") return

    animation.setDirection(-1)
    animation.play()
  }

  selectAnswer(event) {
    const card = event.currentTarget
    const question = card.closest("[data-fact-or-fiction-target='question']")
    if (card.classList.contains("is-selected")) return

    const correct = card.dataset.answer === question.dataset.correctAnswer

    this.cardsFor(question).forEach((questionCard) => {
      const selected = questionCard === card
      questionCard.classList.toggle("is-selected", selected)
      questionCard.setAttribute("aria-pressed", selected ? "true" : "false")
      if (!selected) this.loadHoverAnimation(questionCard)
    })

    this.loadResultAnimation(card, correct)

    question.querySelectorAll("[data-fact-or-fiction-feedback]").forEach((feedback) => {
      feedback.hidden = feedback.dataset.factOrFictionFeedback !== (correct ? "correct" : "wrong")
    })
  }

  previousQuestion(event) {
    event.preventDefault()
    this.showQuestion(this.currentIndex - 1)
  }

  nextQuestion(event) {
    event.preventDefault()
    this.showQuestion(this.currentIndex + 1)
  }

  showQuestion(index) {
    const total = this.questionTargets.length
    if (index < 0 || index >= total) return

    this.questionTargets.forEach((question) => {
      question.hidden = Number(question.dataset.questionIndex) !== index
    })

    this.progressLabelTarget.textContent = `Question ${index + 1} of ${total}`
    this.progressFillTarget.style.width = `${((index + 1) / total) * 100}%`
    this.previousButtonTarget.disabled = index === 0
    this.nextButtonTarget.disabled = index >= total - 1
  }

  loadHoverAnimation(card) {
    this.loadAnimation(card, { kind: "hover", path: card.dataset.hoverUrl, autoplay: false })
  }

  loadResultAnimation(card, correct) {
    this.loadAnimation(card, {
      kind: correct ? "correct" : "wrong",
      path: correct ? card.dataset.correctUrl : card.dataset.wrongUrl,
      autoplay: true
    })
  }

  loadAnimation(card, { kind, path, autoplay }) {
    this.animations.get(card)?.destroy()

    const container = card.querySelector("[data-fact-or-fiction-card-animation]")
    const animation = lottie.loadAnimation({
      container,
      renderer: "svg",
      loop: false,
      autoplay,
      path
    })
    animation.kind = kind
    // The static text label is the no-animation fallback; hide it once the
    // Lottie art (which carries the same word) is in.
    animation.addEventListener("DOMLoaded", () => card.classList.add("has-animation"))
    this.animations.set(card, animation)
  }

  cardsFor(question) {
    return this.cardTargets.filter((card) => question.contains(card))
  }

  get currentIndex() {
    const visible = this.questionTargets.find((question) => !question.hidden)

    return visible ? Number(visible.dataset.questionIndex) : 0
  }
}
